
import { StoreApi } from 'zustand';
import { PriceState } from '../types';
import { UserState } from '../../auth/userSlice';
import { fetchMetalPrices, fetchMetalPriceHistory } from '../priceService';
import { MetalPrice, TimePeriod } from '@/lib/metal-price';
import { GoldPurity, SilverPurity, Unit, calculateGoldPrice, calculateSilverPrice } from '@/lib/pricing';
import { safeLocalStorage } from '@/utils/safeStorage';

type SetPriceState = StoreApi<PriceState & UserState>['setState'];
type GetPriceState = StoreApi<PriceState & UserState>['getState'];

export const setSelectedTimePeriod = (
  set: SetPriceState,
  period: TimePeriod
) => {
  set({ selectedTimePeriod: period });
};

export const setGoldPurity = (set: SetPriceState, get: GetPriceState, purity: GoldPurity) => {
  const { currentGoldPrice } = get();
  const newGoldPrice = currentGoldPrice ? calculateGoldPrice(currentGoldPrice, purity) : 0;
  set({ goldPurity: purity, goldPrice: newGoldPrice });
};
export const setSilverPurity = (set: SetPriceState, get: GetPriceState, purity: SilverPurity) => {
  const { currentSilverPrice, silverUnit } = get();
  const newSilverPrice = currentSilverPrice ? calculateSilverPrice(currentSilverPrice, purity, silverUnit) : 0;
  set({ silverPurity: purity, silverPrice: newSilverPrice });
};
export const setSilverUnit = (set: SetPriceState, get: GetPriceState, unit: Unit) => {
  const { currentSilverPrice, silverPurity } = get();
  const newSilverPrice = currentSilverPrice ? calculateSilverPrice(currentSilverPrice, silverPurity, unit) : 0;
  set({ silverUnit: unit, silverPrice: newSilverPrice });
};

export const fetchCurrentPricesData = async (set: SetPriceState, get: GetPriceState): Promise<void> => {
  try {
    const { gold, silver } = await fetchMetalPrices();
    
    const goldPurity = get().goldPurity;
    const silverPurity = get().silverPurity;
    const silverUnit = get().silverUnit;

    const newGoldPrice = gold ? calculateGoldPrice(gold, goldPurity) : 0;
    const newSilverPrice = silver ? calculateSilverPrice(silver, silverPurity, silverUnit) : 0;
    
    // 🎯 SINGLE SET CALL - Prevents multiple re-renders that interrupt TTS
    set({ 
      currentGoldPrice: gold,
      currentSilverPrice: silver,
      isPriceLoading: false,
      error: null,
      // backward compatibility
      rawGoldPrice: gold,
      rawSilverPrice: silver,
      goldPrice: newGoldPrice,
      silverPrice: newSilverPrice,
    });
  } catch (error: any) {
    console.error('Error in fetchCurrentPricesData action:', error);
    
    // Try to provide fallback data from cache if available
    const fallbackData = tryGetCachedFallbackPrices();
    if (fallbackData) {
      
      const goldPurity = get().goldPurity;
      const silverPurity = get().silverPurity;
      const silverUnit = get().silverUnit;

      const newGoldPrice = fallbackData.gold ? calculateGoldPrice(fallbackData.gold, goldPurity) : 0;
      const newSilverPrice = fallbackData.silver ? calculateSilverPrice(fallbackData.silver, silverPurity, silverUnit) : 0;
      
      // 🎯 SINGLE SET CALL for error state too
      set({ 
        currentGoldPrice: fallbackData.gold,
        currentSilverPrice: fallbackData.silver,
        isPriceLoading: false,
        rawGoldPrice: fallbackData.gold,
        rawSilverPrice: fallbackData.silver,
        goldPrice: newGoldPrice,
        silverPrice: newSilverPrice,
        error: 'Using cached prices - API temporarily unavailable'
      });
    } else {
      // 🎯 SINGLE SET CALL for final error state
      set({ 
        isPriceLoading: false, 
        error: error.message || 'Failed to fetch current prices' 
      });
    }
  }
};

// Helper function to get cached fallback prices
function tryGetCachedFallbackPrices(): { gold: MetalPrice, silver: MetalPrice } | null {
  try {
    // Try to get from current cache first
    import('@/lib/cacheService').then(({ cacheService }) => {
      const cached = cacheService.get('current_metal_prices');
      if (cached && cached.data) {
        return cached.data;
      }
    });
    
    // Fallback to localStorage
    if (typeof window !== 'undefined') {
      const fallback = safeLocalStorage.getItem('fallback_metal_prices');
      if (fallback) {
        const data = JSON.parse(fallback);
        // Use if less than 24 hours old
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data.prices;
        }
      }
    }
    
    return null;
  } catch {
    return null;
  }
}

let currentHistoryFetchPromise: Promise<{ goldPriceHistory: Partial<MetalPrice>[]; silverPriceHistory: Partial<MetalPrice>[] }> | null = null;
let currentHistoryFetchPeriod: TimePeriod | null = null;

export const fetchPriceHistoryData = async (
  set: SetPriceState,
  period: TimePeriod
): Promise<{ goldPriceHistory: Partial<MetalPrice>[]; silverPriceHistory: Partial<MetalPrice>[] }> => {
  // Dedup: reuse in-flight request for same period
  if (currentHistoryFetchPromise && currentHistoryFetchPeriod === period) {
    return currentHistoryFetchPromise;
  }

  set({ isHistoryLoading: true, error: null });

  const fetchPromise = (async () => {
    try {
      const { goldHistory, silverHistory } = await fetchMetalPriceHistory(period);
      
      set({ 
        goldPriceHistory: goldHistory,
        silverPriceHistory: silverHistory,
        isHistoryLoading: false,
      });
      
      return {
        goldPriceHistory: goldHistory,
        silverPriceHistory: silverHistory
      };
    } catch (error: any) {
      console.error('Error in fetchPriceHistoryData action:', error);
      set({ isHistoryLoading: false, error: error.message || 'Failed to fetch history' });
      return { goldPriceHistory: [], silverPriceHistory: [] };
    } finally {
      currentHistoryFetchPromise = null;
      currentHistoryFetchPeriod = null;
    }
  })();

  currentHistoryFetchPromise = fetchPromise;
  currentHistoryFetchPeriod = period;

  return fetchPromise;
};
